import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Star, Clock } from 'lucide-react';
import { AuthContext } from '../App';
import UpgradeModal from './UpgradeModal';

const YourConnections = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [userMatches, setUserMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userTier, setUserTier] = useState('free');

  useEffect(() => {
    fetchUserMatches();
    fetchUserTier();
  }, []);

  const fetchUserTier = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserTier(data.subscription_tier || 'free');
      }
    } catch (error) {
      console.error('Error fetching user tier:', error);
    }
  };

  const fetchUserMatches = async () => {
    try {
      const timestamp = Date.now();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user-matches/confirmed?t=${timestamp}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserMatches(data.matches || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user matches:', error);
      setLoading(false);
    }
  };

  const startChat = async (userId, matchId) => {
    console.log('🔍 Start chat clicked. User tier:', userTier);
    
    // Check if user is free tier (frontend check)
    if (userTier === 'free') {
      console.log('🚫 Free user - showing upgrade modal');
      setShowUpgradeModal(true);
      return;
    }
    
    console.log('✅ Premium/VIP user - proceeding to chat');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/match/${matchId}/conversation`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/chat/${data.conversation_id}`);
      } else if (response.status === 403) {
        console.log('🚫 Backend blocked - free user confirmed, showing upgrade modal');
        setShowUpgradeModal(true);
      } else {
        alert('Failed to start chat. Please try again.');
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 p-4 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">💚</span> Your Connections ({userMatches.length})
            </h1>
            <p className="text-white/90 text-sm mt-1">Your cherished connections, ready to engage</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {userMatches.map((match) => (
            <div key={match.match_id} className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-400 to-pink-600 flex items-center justify-center text-white text-3xl font-bold mb-4">
                  {match.user.photo_url ? (
                    <img 
                      src={match.user.photo_url} 
                      alt={match.user.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    match.user.name?.charAt(0) || '?'
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{match.user.name}</h3>
                <p className="text-white/80 text-sm mb-1">{match.user.age} years old</p>
                <p className="text-white/80 text-sm mb-4">
                  {match.user.city}, {match.user.state}
                </p>
                
                {match.user.bio && (
                  <p className="text-white/70 text-sm mb-4 line-clamp-3">{match.user.bio}</p>
                )}
                
                {/* Memorable Moments Section */}
                {(match.my_memorable_moment || match.their_memorable_moment) && (
                  <div className="w-full mb-4 space-y-3">
                    {/* What I wrote about them */}
                    {match.my_memorable_moment && (
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-lg">💭</span>
                          <p className="text-white/60 text-xs font-medium uppercase tracking-wide">What stood out to you</p>
                        </div>
                        <p className="text-white text-sm italic leading-relaxed">
                          "{match.my_memorable_moment}"
                        </p>
                      </div>
                    )}
                    
                    {/* What they wrote about me */}
                    {match.their_memorable_moment && (
                      <div className="bg-gradient-to-br from-rose-500/20 to-pink-500/20 backdrop-blur-sm rounded-xl p-4 border border-rose-300/30">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-lg">✨</span>
                          <p className="text-white/80 text-xs font-medium uppercase tracking-wide">What stood out to them</p>
                        </div>
                        <p className="text-white text-sm italic leading-relaxed">
                          "{match.their_memorable_moment}"
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* View Profile Button */}
                  <button
                    onClick={() => navigate(`/profile/match/${match.match_id}`)}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-4 px-6 rounded-full font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center justify-center space-x-2"
                  >
                    <Star className="h-5 w-5" />
                    <span>View Full Profile</span>
                  </button>
                  
                  {/* Speed Date button - always show */}
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        
                        // STEP 1: Check user's session limit FIRST (before anything else)
                        const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        
                        if (userResponse.ok) {
                          const userData = await userResponse.json();
                          const userTier = userData.subscription_tier || 'free';
                          
                          // Check if free user - they have session limits
                          if (userTier === 'free') {
                            // Check remaining sessions
                            const limitResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/usage/speed_dating_sessions`, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            
                            if (limitResponse.ok) {
                              const limitData = await limitResponse.json();
                              
                              // If limit reached, show upgrade message
                              if (limitData.usage_count >= limitData.limit) {
                                alert(`⚠️ Speed Date Limit Reached\n\nYou've used all ${limitData.limit} free speed dates this month!\n\nUpgrade to Premium for unlimited speed dating sessions.\n\nClick OK to view upgrade options.`);
                                navigate('/subscription/plans');
                                return; // Stop here - don't proceed
                              }
                            }
                          }
                        }
                        
                        // STEP 2: Check if partner is busy (queue system)
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/request`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            match_id: match.match_id
                          })
                        });
                        
                        const result = await response.json();
                        
                        // Check for email verification required error
                        if (response.status === 403 && result.detail?.action === 'verify_email') {
                          alert('📧 Email Verification Required\n\nPlease verify your email to start speed dating.\n\nCheck your inbox for the confirmation link.');
                          return;
                        }
                        
                        if (!response.ok) {
                          throw new Error(result.detail?.message || result.detail || 'Failed to request speed date');
                        }
                        
                        // Check if target user has exhausted their sessions
                        if (result.target_limit_reached) {
                          alert(`⚠️ Unable to Start Speed Date\n\n${result.target_name} has used all their free speed dating sessions this month.\n\nThey need to upgrade to Premium to continue dating.`);
                          return; // Don't navigate
                        }
                        
                        // Check if user was queued
                        if (result.queued) {
                          alert(`${match.user.name} is currently in another date.\n\nYou're #${result.position} in queue!\n\nWe'll notify you when they're available.`);
                          return; // Don't navigate
                        }
                        
                        // STEP 3: All checks passed - proceed to speed dating
                        navigate(`/speed-dating-session`, {
                          state: { 
                            partnerInfo: {
                              id: match.user.id,
                              name: match.user.name,
                              photo_url: match.user.photo_url
                            },
                            skipQueueCheck: true  // Already checked availability
                          }
                        });
                      } catch (error) {
                        console.error('Error requesting speed date:', error);
                        alert(error.message || 'Failed to start speed date. Please try again.');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-4 px-6 rounded-full font-semibold hover:from-rose-600 hover:to-pink-700 transition-all flex items-center justify-center space-x-2"
                  >
                    <Clock className="h-5 w-5" />
                    <span>Start Speed Date</span>
                  </button>
                  
                  {/* Chat button - always show, but check tier on click */}
                  <button
                    onClick={() => startChat(match.user.id, match.match_id)}
                    className="w-full bg-white/20 text-white py-4 px-6 rounded-full font-semibold hover:bg-white/30 transition-colors flex items-center justify-center space-x-2"
                  >
                    <MessageCircle className="h-5 w-5" />
                    <span>Chat</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {userMatches.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Heart className="h-16 w-16 text-white/50 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Matches Yet</h3>
              <p className="text-white/80">Keep browsing to find your perfect match!</p>
              <button
                onClick={() => navigate('/matches')}
                className="mt-6 bg-white text-rose-500 px-6 py-3 rounded-full font-semibold hover:bg-rose-50 transition-colors"
              >
                Browse Matches
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[9999]">
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            feature="chat"
            message="Upgrade to Premium or VIP to chat with your matches!"
          />
        </div>
      )}
    </div>
  );
};

export default YourConnections;
